import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useHasRole } from "@/hooks/useHasRole";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { format, differenceInDays } from "date-fns";
import { Loader2, ShieldAlert, Filter, ClipboardList, Users, AlertOctagon, FileBarChart, Wand2 } from "lucide-react";
import { DPOTeamManager } from "@/components/dpo/DPOTeamManager";
import { DSRAttachments } from "@/components/dpo/DSRAttachments";
import { IncidentsTab } from "@/components/dpo/IncidentsTab";
import { ReportsTab } from "@/components/dpo/ReportsTab";

interface Dsr {
  id: string;
  requester_email: string;
  requester_name: string | null;
  requester_user_id: string | null;
  request_type: string;
  scope: string;
  patient_id: string | null;
  company_id: string | null;
  status: string;
  received_at: string;
  due_at: string;
  extended_due_at: string | null;
  assigned_to: string | null;
  resolution: string | null;
  subject_identification: Record<string, unknown>;
}

interface DsrEvent {
  id: string;
  event_type: string;
  actor_user_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

const REQUEST_TYPE_LABELS: Record<string, string> = {
  access: "Acesso",
  rectification: "Correção",
  deletion: "Exclusão",
  portability: "Portabilidade",
  revoke_consent: "Revogação consentimento",
  info_sharing: "Compartilhamento",
  automated_decision_review: "Revisão decisão",
  confirmation: "Confirmação",
};

const STATUS_LABELS: Record<string, string> = {
  received: "Recebida",
  under_review: "Em análise",
  in_progress: "Em andamento",
  fulfilled: "Concluída",
  rejected: "Rejeitada",
  extended: "Prazo prorrogado",
};

const STATUS_OPEN = ["received", "under_review", "in_progress", "extended"];
const STATUS_OPTIONS = Object.keys(STATUS_LABELS);

function statusVariant(status: string): "default" | "destructive" | "secondary" | "outline" {
  if (status === "fulfilled") return "default";
  if (status === "rejected") return "destructive";
  if (status === "extended") return "secondary";
  return "outline";
}

function deadlineLabel(dsr: Dsr): { label: string; tone: "ok" | "warn" | "late" } {
  const due = new Date(dsr.extended_due_at ?? dsr.due_at);
  const days = differenceInDays(due, new Date());
  if (days < 0) return { label: `Atrasada ${Math.abs(days)}d`, tone: "late" };
  if (days <= 5) return { label: `${days}d restantes`, tone: "warn" };
  return { label: `${days}d restantes`, tone: "ok" };
}

export default function DPODashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasRole = useHasRole("security_officer");

  const [dsrs, setDsrs] = useState<Dsr[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("open");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Dsr | null>(null);
  const [events, setEvents] = useState<DsrEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [note, setNote] = useState("");
  const [resolutionDraft, setResolutionDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const loadDsrs = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("data_subject_requests")
      .select("*")
      .order("due_at", { ascending: true });
    setDsrs((data as Dsr[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (hasRole) void loadDsrs();
  }, [hasRole, loadDsrs]);

  const loadEvents = useCallback(async (id: string) => {
    setEventsLoading(true);
    const { data } = await supabase
      .from("data_subject_request_events")
      .select("*")
      .eq("request_id", id)
      .order("created_at", { ascending: false });
    setEvents((data as DsrEvent[]) ?? []);
    setEventsLoading(false);
  }, []);

  const openDetails = (d: Dsr) => {
    setSelected(d);
    setNote("");
    setResolutionDraft(d.resolution ?? "");
    setStatusDraft(d.status);
    void loadEvents(d.id);
  };

  const closeDetails = () => setSelected(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dsrs.filter((d) => {
      if (statusFilter === "open" && !STATUS_OPEN.includes(d.status)) return false;
      if (statusFilter !== "open" && statusFilter !== "all" && d.status !== statusFilter) return false;
      if (!q) return true;
      return (
        d.requester_email.toLowerCase().includes(q) ||
        (d.requester_name ?? "").toLowerCase().includes(q) ||
        d.id.toLowerCase().includes(q)
      );
    });
  }, [dsrs, statusFilter, search]);

  const metrics = useMemo(() => {
    const now = new Date();
    const open = dsrs.filter((d) => STATUS_OPEN.includes(d.status));
    const overdue = open.filter((d) => new Date(d.extended_due_at ?? d.due_at) < now);
    const dueSoon = open.filter((d) => {
      const due = new Date(d.extended_due_at ?? d.due_at);
      const days = differenceInDays(due, now);
      return days >= 0 && days <= 5;
    });
    const fulfilled = dsrs.filter((d) => d.status === "fulfilled");
    return { open: open.length, overdue: overdue.length, dueSoon: dueSoon.length, fulfilled: fulfilled.length };
  }, [dsrs]);

  const handleAssignToMe = async () => {
    if (!selected || !user) return;
    setSaving(true);
    const { error } = await supabase
      .from("data_subject_requests")
      .update({ assigned_to: user.id, status: "under_review" })
      .eq("id", selected.id);
    if (!error) {
      await supabase.from("data_subject_request_events").insert({
        request_id: selected.id,
        event_type: "assigned",
        actor_user_id: user.id,
        payload: { assigned_to: user.id },
      });
      toast.success("Atribuído a você");
      await loadDsrs();
      await loadEvents(selected.id);
      setSelected({ ...selected, assigned_to: user.id, status: "under_review" });
      setStatusDraft("under_review");
    } else {
      toast.error(`Falhou: ${error.message}`);
    }
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!selected || !user || !note.trim()) return;
    setSaving(true);
    const { error } = await supabase.from("data_subject_request_events").insert({
      request_id: selected.id,
      event_type: "note",
      actor_user_id: user.id,
      payload: { text: note.trim() },
    });
    if (!error) {
      setNote("");
      await loadEvents(selected.id);
      toast.success("Nota adicionada");
    } else {
      toast.error(`Falhou: ${error.message}`);
    }
    setSaving(false);
  };

  const handleSaveResolution = async () => {
    if (!selected || !user) return;
    setSaving(true);
    const updates = { status: statusDraft, resolution: resolutionDraft };
    const { error } = await supabase
      .from("data_subject_requests")
      .update(updates)
      .eq("id", selected.id);
    if (!error) {
      // Nota: o evento "status_changed" é criado automaticamente pelo trigger
      // notify_dsr_subject_on_status_change no banco. Inserimos aqui apenas o
      // evento "responded" (com a resolução), que não é gerado pelo trigger.
      if (statusDraft === "fulfilled" || statusDraft === "rejected") {
        await supabase.from("data_subject_request_events").insert({
          request_id: selected.id,
          event_type: "responded",
          actor_user_id: user.id,
          payload: { resolution: resolutionDraft },
          is_public: true,
        });
      }
      toast.success("Solicitação atualizada");
      await loadDsrs();
      await loadEvents(selected.id);
      setSelected({ ...selected, ...updates } as Dsr);
    } else {
      toast.error(`Falhou: ${error.message}`);
    }
    setSaving(false);
  };

  const handleExtendDeadline = async () => {
    if (!selected || !user) return;
    setSaving(true);
    const newDue = new Date(new Date(selected.due_at).getTime() + 15 * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
      .from("data_subject_requests")
      .update({ extended_due_at: newDue, status: "extended" })
      .eq("id", selected.id);
    if (!error) {
      // Os eventos "deadline_extended" e "status_changed" são criados
      // automaticamente pelo trigger notify_dsr_subject_on_status_change.
      toast.success("Prazo prorrogado por 15 dias");
      await loadDsrs();
      await loadEvents(selected.id);
      setSelected({ ...selected, extended_due_at: newDue, status: "extended" });
      setStatusDraft("extended");
    } else {
      toast.error(`Falhou: ${error.message}`);
    }
    setSaving(false);
  };

  const handleAutoFulfill = async (action: "auto_export" | "auto_revoke_consent" | "mark_deletion_ready") => {
    if (!selected) return;
    setSaving(true);
    const { error } = await supabase.functions.invoke("dsr-fulfill", {
      body: { request_id: selected.id, action },
    });
    if (error) {
      toast.error(`Falhou: ${error.message}`);
    } else {
      toast.success("Ação executada com sucesso");
      await loadEvents(selected.id);
      await loadDsrs();
    }
    setSaving(false);
  };

  if (hasRole === null) {
    return (
      <AppLayout title="Painel DPO" showBack onBack={() => navigate("/app")} showFAB={false}>
        <div className="p-6"><Skeleton className="h-32 w-full" /></div>
      </AppLayout>
    );
  }

  if (!hasRole) {
    return (
      <AppLayout title="Painel DPO" showBack onBack={() => navigate("/app")} showFAB={false}>
        <Card className="m-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="w-5 h-5" /> Acesso restrito
            </CardTitle>
            <CardDescription>
              Apenas usuários com o papel <strong>Encarregado de Dados (DPO)</strong> podem acessar esta área.
            </CardDescription>
          </CardHeader>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Painel DPO" showBack onBack={() => navigate("/app")} showFAB={false}>
      <div className="container max-w-6xl mx-auto p-4 space-y-4">
        <Tabs defaultValue="requests">
          <div className="overflow-x-auto -mx-4 px-4 scrollbar-none">
            <TabsList className="w-max">
              <TabsTrigger value="requests" className="gap-2 whitespace-nowrap">
                <ClipboardList className="w-4 h-4" /> Solicitações
              </TabsTrigger>
              <TabsTrigger value="incidents" className="gap-2 whitespace-nowrap">
                <AlertOctagon className="w-4 h-4" /> Incidentes
              </TabsTrigger>
              <TabsTrigger value="reports" className="gap-2 whitespace-nowrap">
                <FileBarChart className="w-4 h-4" /> Relatórios
              </TabsTrigger>
              <TabsTrigger value="team" className="gap-2 whitespace-nowrap">
                <Users className="w-4 h-4" /> Encarregados
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="requests" className="space-y-4 mt-4">
            {/* Métricas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Em aberto" value={metrics.open} />
              <MetricCard label="Atrasadas" value={metrics.overdue} tone="destructive" />
              <MetricCard label="Vencendo (≤5d)" value={metrics.dueSoon} tone="warn" />
              <MetricCard label="Concluídas" value={metrics.fulfilled} />
            </div>

            {/* Filtros */}
            <Card>
              <CardContent className="pt-4 flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Em aberto</SelectItem>
                      <SelectItem value="all">Todas</SelectItem>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  placeholder="Buscar por e-mail, nome ou ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1"
                />
              </CardContent>
            </Card>

            {/* Lista */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ClipboardList className="w-4 h-4" /> Solicitações ({filtered.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-32 w-full" />
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma solicitação encontrada.</p>
                ) : (
                  <ul className="divide-y">
                    {filtered.map((d) => {
                      const dl = deadlineLabel(d);
                      return (
                        <li key={d.id}>
                          <button
                            type="button"
                            onClick={() => openDetails(d)}
                            className="w-full text-left py-3 flex flex-wrap items-center gap-3 hover:bg-muted/40 rounded px-2 -mx-2"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">
                                {REQUEST_TYPE_LABELS[d.request_type] ?? d.request_type} ·{" "}
                                <span className="text-muted-foreground">{d.requester_email}</span>
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Recebida em {format(new Date(d.received_at), "dd/MM/yyyy HH:mm")}
                              </p>
                            </div>
                            <Badge variant={statusVariant(d.status)}>{STATUS_LABELS[d.status] ?? d.status}</Badge>
                            <span
                              className={
                                "text-xs px-2 py-0.5 rounded " +
                                (dl.tone === "late"
                                  ? "bg-destructive/15 text-destructive"
                                  : dl.tone === "warn"
                                  ? "bg-warning/20 text-warning-foreground"
                                  : "bg-muted text-muted-foreground")
                              }
                            >
                              {dl.label}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="incidents" className="mt-4">
            <IncidentsTab />
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <ReportsTab />
          </TabsContent>

          <TabsContent value="team" className="mt-4">
            <DPOTeamManager />
          </TabsContent>
        </Tabs>
      </div>

      {/* Detalhes */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDetails()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {REQUEST_TYPE_LABELS[selected.request_type] ?? selected.request_type}
                </DialogTitle>
                <DialogDescription>
                  ID {selected.id.slice(0, 8)} · {selected.requester_email}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Solicitante">
                    {selected.requester_name ?? "—"}
                  </Field>
                  <Field label="Conta vinculada">
                    {selected.requester_user_id ? "Sim" : "Não"}
                  </Field>
                  <Field label="Recebida em">
                    {format(new Date(selected.received_at), "dd/MM/yyyy HH:mm")}
                  </Field>
                  <Field label="Prazo legal">
                    {format(new Date(selected.extended_due_at ?? selected.due_at), "dd/MM/yyyy")}
                    {selected.extended_due_at && (
                      <Badge variant="secondary" className="ml-2 text-xs">prorrogado</Badge>
                    )}
                  </Field>
                </div>

                {Object.keys(selected.subject_identification ?? {}).length > 0 && (
                  <Field label="Identificação do titular">
                    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                      {JSON.stringify(selected.subject_identification, null, 2)}
                    </pre>
                  </Field>
                )}

                {/* Ações DPO */}
                <div className="border-t pt-3 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={handleAssignToMe} disabled={saving}>
                      Atribuir a mim
                    </Button>
                    {!selected.extended_due_at && (
                      <Button size="sm" variant="outline" onClick={handleExtendDeadline} disabled={saving}>
                        Prorrogar +15 dias
                      </Button>
                    )}
                    {(selected.request_type === "access" || selected.request_type === "portability") && selected.requester_user_id && (
                      <Button size="sm" variant="secondary" onClick={() => handleAutoFulfill("auto_export")} disabled={saving}>
                        <Wand2 className="w-4 h-4 mr-1" /> Gerar export automático
                      </Button>
                    )}
                    {selected.request_type === "revoke_consent" && selected.requester_user_id && (
                      <Button size="sm" variant="secondary" onClick={() => handleAutoFulfill("auto_revoke_consent")} disabled={saving}>
                        <Wand2 className="w-4 h-4 mr-1" /> Revogar consentimentos
                      </Button>
                    )}
                    {selected.request_type === "deletion" && (
                      <Button size="sm" variant="secondary" onClick={() => handleAutoFulfill("mark_deletion_ready")} disabled={saving}>
                        <Wand2 className="w-4 h-4 mr-1" /> Marcar pronto p/ exclusão
                      </Button>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label>Status</Label>
                    <Select value={statusDraft} onValueChange={setStatusDraft}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Resolução / evidência</Label>
                    <Textarea
                      rows={3}
                      value={resolutionDraft}
                      onChange={(e) => setResolutionDraft(e.target.value)}
                      placeholder="Descreva como a solicitação foi atendida..."
                    />
                  </div>
                  <Button size="sm" onClick={handleSaveResolution} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Salvar resolução
                  </Button>
                </div>

                {/* Anexos de evidência */}
                <div className="border-t pt-3">
                  <DSRAttachments requestId={selected.id} canManage={true} />
                </div>

                {/* Notas */}
                <div className="border-t pt-3 space-y-2">
                  <Label>Adicionar nota interna</Label>
                  <Textarea
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Anotação para o histórico..."
                  />
                  <Button size="sm" variant="secondary" onClick={handleAddNote} disabled={saving || !note.trim()}>
                    Adicionar
                  </Button>
                </div>

                {/* Trilha de eventos */}
                <div className="border-t pt-3">
                  <Label className="mb-2 block">Trilha de eventos</Label>
                  {eventsLoading ? (
                    <Skeleton className="h-20 w-full" />
                  ) : events.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum evento.</p>
                  ) : (
                    <ul className="space-y-2 max-h-60 overflow-y-auto">
                      {events.map((ev) => (
                        <li key={ev.id} className="text-xs border-l-2 border-muted pl-2">
                          <p className="font-medium">{ev.event_type}</p>
                          <p className="text-muted-foreground">
                            {format(new Date(ev.created_at), "dd/MM/yyyy HH:mm")}
                          </p>
                          {Object.keys(ev.payload ?? {}).length > 0 && (
                            <pre className="text-[10px] bg-muted p-1 rounded mt-1 overflow-x-auto">
                              {JSON.stringify(ev.payload, null, 2)}
                            </pre>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeDetails}>Fechar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function MetricCard({ label, value, tone }: { label: string; value: number; tone?: "destructive" | "warn" }) {
  const color =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warn"
      ? "text-warning"
      : "text-foreground";
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}
